// the bumper reel: with ?reel=1 in the URL, a panel under the player lists every bumper the clock can air (station
// IDs, feature intros and the outro, dayparts and the night sign-on, specials, interrupts), the lines left in the
// drawer, and the bare stings, each with a play button, and plays them all in order on request. For hearing and
// judging them without waiting for the clock; it changes nothing about what airs.
import { escapeHtml } from '../utils/string.mjs';
import { parseQueryString } from '../utils/setting.mjs';
import { playBumper, isPlaying as bumperOn, linesReady } from './bumper.mjs';
import { FEATURE_TITLES } from './cadence.mjs';

const { clock, seasons } = window.RHL;
const { voice } = clock;
const AUDIO = 'rhl/audio';
const STATION = ['Russian Hill', 'Local'];

// every bumper that can air, grouped, as [label, bumper spec]
const onAir = () => {
	const groups = [];
	groups.push(['Station IDs at :00 and :30', voice.stationId.lines.map((line) => [line, {
		title: STATION, line, sting: 'bell-2', upNext: 'Bay &amp; Tides',
	}])]);
	groups.push(['Feature intros, and the outro back to the loop', [
		...Object.entries(voice.featureIntros).flatMap(([feature, lines]) => lines.map((line) => [line, {
			title: FEATURE_TITLES[feature],
			line,
			sting: 'bell-2',
			upNext: {
				'market-closeup': 'Market Close-Up', 'around-town': 'Around Town', 'news-desk': 'News Desk', 'views-block': 'Views Around the Bay', 'live-look': 'Live Look',
			}[feature],
		}])),
		['outro', {
			// outros are silent (Oct 27, Q17)
			title: STATION, line: null, sting: null, upNext: 'Current Conditions',
		}],
	]]);
	groups.push(['Dayparts, at :00 when one begins', [
		[voice.dayparts.morning, { title: STATION, line: voice.dayparts.morning, sting: 'bell-2' }],
		[voice.dayparts.weekendMorning, { title: STATION, line: voice.dayparts.weekendMorning, sting: 'bell-2' }],
		[voice.dayparts.evening, { title: STATION, line: voice.dayparts.evening, sting: 'bell-2' }],
		[voice.dayparts.night, { title: ['Good', 'Night'], line: voice.dayparts.night, sting: 'foghorn' }],
		[voice.dayparts.nightFog, { title: ['Good', 'Night'], line: voice.dayparts.nightFog, sting: 'foghorn' }],
	]]);
	groups.push(['Specials, at :30 on their days', [
		['4C', { title: STATION, line: '4C', sting: 'bell-2' }],
		...seasons.lines.map((s) => [s.line, { title: STATION, line: s.line, sting: 'bell-2' }, `${s.from}${s.to !== s.from ? ` to ${s.to}` : ''}`]),
	]]);
	groups.push(['Interrupts', [
		[voice.interrupts.weather, {
			title: ['Weather', 'Statement'], line: voice.interrupts.weather, sting: 'foghorn', alert: true,
		}],
		[voice.interrupts.quake, {
			title: ['Earthquake', 'Information'], line: voice.interrupts.quake, sting: 'bell-2', alert: true,
		}],
		[voice.interrupts.halt, {
			title: ['Trading', 'Halt'], line: voice.interrupts.halt, sting: 'bell-2', alert: true,
		}],
		[voice.interrupts.close, {
			title: STATION, line: voice.interrupts.close, sting: 'bell-1', upNext: 'Current Conditions',
		}],
	]]);
	return groups;
};

const render = (panel, lines) => {
	const groups = onAir();
	const used = new Set(groups.flatMap(([, items]) => items.map(([, spec]) => spec.line)).filter(Boolean));
	const drawer = Object.values(lines).filter((l) => !used.has(l.id))
		.map((l) => [l.id, { title: STATION, line: l.id, sting: 'bell-2' }]);
	const all = [...groups, ['In the drawer (not in the cadence)', drawer]];
	const specs = [];
	const row = ([label, spec, note]) => {
		specs.push(spec);
		const text = spec.line ? lines[spec.line]?.text ?? '' : `${spec.sting}, no voice`;
		return `<li><button type="button" data-spec="${specs.length - 1}">Play</button>
			<span class="id">${escapeHtml(label)}</span> <span class="text">${escapeHtml(text)}</span>${note ? ` <span class="note">${escapeHtml(note)}</span>` : ''}</li>`;
	};
	panel.innerHTML = `<h2>Bumper reel</h2>
		<p>Each plays the full bumper on the player above, with sound. "Play all on air" runs every bumper the clock can air, in order.</p>
		<p><button type="button" class="all">Play all on air</button> <button type="button" class="stop">Stop after this one</button>
		<span class="stings">Stings: ${['bell-2', 'bell-1', 'foghorn'].map((s) => `<button type="button" data-sting="${s}">${s}</button>`).join(' ')}</span></p>
		${all.map(([name, items]) => `<h3>${escapeHtml(name)}</h3><ol>${items.map(row).join('')}</ol>`).join('')}`;
	return { specs, onAirCount: groups.reduce((n, [, items]) => n + items.length, 0) };
};

const init = async () => {
	const query = parseQueryString();
	if (query.reel !== '1' && query.controls !== '1') return;
	await linesReady;
	const lines = await fetch(`${AUDIO}/voice.json`).then((r) => r.json()).then((b) => Object.fromEntries(b.lines.map((l) => [l.id, l])));
	const panel = document.createElement('section');
	panel.id = 'rhl-reel';
	(document.querySelector('#rhl-control') ?? document.querySelector('#divTwc'))?.after(panel);
	const { specs, onAirCount } = render(panel, lines);
	let stopAll = false;

	const playOne = async (spec) => {
		document.querySelector('#divTwc')?.scrollIntoView({ block: 'nearest' });
		await playBumper(spec);
	};
	panel.addEventListener('click', async (event) => {
		const button = event.target.closest('button');
		if (!button || bumperOn()) return;
		if (button.dataset.sting) {
			new Audio(`${AUDIO}/${button.dataset.sting}.wav`).play();
		} else if (button.dataset.spec) {
			await playOne(specs[Number(button.dataset.spec)]);
		} else if (button.classList.contains('all')) {
			stopAll = false;
			for (let i = 0; i < onAirCount && !stopAll; i += 1) {
				// eslint-disable-next-line no-await-in-loop
				await playOne(specs[i]);
			}
		} else if (button.classList.contains('stop')) {
			stopAll = true;
		}
	});
	// the stop button works mid-bumper too
	panel.querySelector('.stop').addEventListener('click', () => {
		stopAll = true;
	});
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
